import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Network, Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { getSupervisorAnalytics, SupervisorAnalyticsRow } from '../../services/annotationApi';
import { UserEmail } from '../../components/common/UserEmail';
import { formatHm } from '../../utils/formatDuration';

type SortField =
  | 'name'
  | 'teamSize'
  | 'recordingSeconds'
  | 'submittedSeconds'
  | 'supervisorApprovedSeconds'
  | 'rejectedSeconds'
  | 'tasksFinallyAccepted'
  | 'avgHoursToSupervisorApprove'
  | 'avgHoursToFinalAccept';

type SortDir = 'asc' | 'desc';

type DateMode = 'all' | '24h' | '7d' | '30d' | 'custom';

function presetRange(mode: DateMode): { startDate?: string; endDate?: string } {
  const now = new Date();
  switch (mode) {
    case '24h':
      return { startDate: new Date(now.getTime() - 24 * 3600 * 1000).toISOString() };
    case '7d':
      return { startDate: new Date(now.getTime() - 7 * 24 * 3600 * 1000).toISOString() };
    case '30d':
      return { startDate: new Date(now.getTime() - 30 * 24 * 3600 * 1000).toISOString() };
    default:
      return {};
  }
}

function SortHeader({
  label,
  field,
  sort,
  onSort,
  numeric = false,
}: {
  label: string;
  field: SortField;
  sort: { field: SortField; dir: SortDir };
  onSort: (f: SortField) => void;
  numeric?: boolean;
}) {
  const active = sort.field === field;
  const Icon = !active ? ChevronsUpDown : sort.dir === 'desc' ? ChevronDown : ChevronUp;
  return (
    <th
      onClick={() => onSort(field)}
      className={`px-4 py-3 text-${numeric ? 'right' : 'left'} text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700`}
    >
      <span className={`inline-flex items-center gap-1 ${numeric ? 'flex-row-reverse' : ''}`}>
        {label}
        <Icon size={12} className={active ? 'text-violet-600' : 'text-gray-400'} />
      </span>
    </th>
  );
}

export default function SupervisorAnalyticsPage() {
  const [search, setSearch] = useState('');
  const [dateMode, setDateMode] = useState<DateMode>('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: 'recordingSeconds',
    dir: 'desc',
  });

  const dateRange = useMemo(() => {
    if (dateMode === 'custom') {
      return {
        startDate: customStart ? new Date(customStart).toISOString() : undefined,
        endDate: customEnd ? new Date(customEnd + 'T23:59:59').toISOString() : undefined,
      };
    }
    return presetRange(dateMode);
  }, [dateMode, customStart, customEnd]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['supervisor-analytics', dateRange.startDate ?? '', dateRange.endDate ?? ''],
    queryFn: () => getSupervisorAnalytics(dateRange),
  });

  const toggleSort = (field: SortField) =>
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { field, dir: field === 'name' ? 'asc' : 'desc' },
    );

  const sorted = useMemo(() => {
    const factor = sort.dir === 'desc' ? -1 : 1;
    const filtered = rows.filter((r) =>
      `${r.supervisorName} ${r.supervisorEmail}`.toLowerCase().includes(search.toLowerCase()),
    );
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sort.field) {
        case 'name':
          cmp = a.supervisorName.localeCompare(b.supervisorName);
          break;
        case 'teamSize':
          cmp = a.teamSize - b.teamSize;
          break;
        case 'recordingSeconds':
          cmp = a.recordingSeconds - b.recordingSeconds;
          break;
        case 'submittedSeconds':
          cmp = a.submittedSeconds - b.submittedSeconds;
          break;
        case 'supervisorApprovedSeconds':
          cmp = a.supervisorApprovedSeconds - b.supervisorApprovedSeconds;
          break;
        case 'rejectedSeconds':
          cmp = a.rejectedSeconds - b.rejectedSeconds;
          break;
        case 'tasksFinallyAccepted':
          cmp = a.tasksFinallyAccepted - b.tasksFinallyAccepted;
          break;
        case 'avgHoursToSupervisorApprove':
          cmp = (a.avgHoursToSupervisorApprove ?? Infinity) - (b.avgHoursToSupervisorApprove ?? Infinity);
          break;
        case 'avgHoursToFinalAccept':
          cmp = (a.avgHoursToFinalAccept ?? Infinity) - (b.avgHoursToFinalAccept ?? Infinity);
          break;
      }
      return cmp * factor;
    });
  }, [rows, sort, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Network size={20} className="text-indigo-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Supervisor Analytics</h1>
          <p className="text-sm text-gray-500">
            Recording hours flowing through each supervisor's subtree (enumerators + transcribers):
            received → transcribed &amp; submitted → approved at the supervisor level.
          </p>
        </div>
      </div>

      {/* Date + search filters */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {(['24h', '7d', '30d', 'all'] as DateMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setDateMode(m)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                dateMode === m
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {m === 'all' ? 'All Time' : `Last ${m}`}
            </button>
          ))}
          <button
            onClick={() => setDateMode('custom')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              dateMode === 'custom'
                ? 'bg-violet-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Custom
          </button>
          {dateMode === 'custom' && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="input max-w-[10rem]"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="input max-w-[10rem]"
              />
            </>
          )}
        </div>
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input pl-9"
            placeholder="Search by supervisor name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader label="Supervisor"            field="name"                          sort={sort} onSort={toggleSort} />
                <SortHeader label="Team"                  field="teamSize"                      sort={sort} onSort={toggleSort} numeric />
                <SortHeader label="Recording hrs"         field="recordingSeconds"              sort={sort} onSort={toggleSort} numeric />
                <SortHeader label="Transcribed & Submitted hrs" field="submittedSeconds"        sort={sort} onSort={toggleSort} numeric />
                <SortHeader label="Sup. Approved hrs"     field="supervisorApprovedSeconds"     sort={sort} onSort={toggleSort} numeric />
                <SortHeader label="Rejected hrs"          field="rejectedSeconds"               sort={sort} onSort={toggleSort} numeric />
                <SortHeader label="Final Accepted"        field="tasksFinallyAccepted"          sort={sort} onSort={toggleSort} numeric />
                <SortHeader label="Avg h→Sup Approve"     field="avgHoursToSupervisorApprove"   sort={sort} onSort={toggleSort} numeric />
                <SortHeader label="Avg h→Final Accept"    field="avgHoursToFinalAccept"         sort={sort} onSort={toggleSort} numeric />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">Loading…</td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No supervisors found in the selected window.
                  </td>
                </tr>
              ) : (
                sorted.map((r: SupervisorAnalyticsRow) => (
                  <tr key={r.supervisorId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{r.supervisorName}</div>
                      <UserEmail email={r.supervisorEmail} role="supervisor" />
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className="text-gray-900 font-semibold">{r.teamSize}</span>
                      <span className="text-gray-400 text-xs ml-1">
                        ({r.teamEnumerators}e + {r.teamTranscribers}t)
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 font-semibold">{formatHm(r.recordingSeconds)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{formatHm(r.submittedSeconds)}</td>
                    <td className="px-4 py-3 text-sm text-right text-violet-700 font-semibold">{formatHm(r.supervisorApprovedSeconds)}</td>
                    <td className="px-4 py-3 text-sm text-right text-red-600">{formatHm(r.rejectedSeconds)}</td>
                    <td className="px-4 py-3 text-sm text-right text-emerald-700 font-semibold">{r.tasksFinallyAccepted}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">
                      {r.avgHoursToSupervisorApprove != null ? `${r.avgHoursToSupervisorApprove}h` : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">
                      {r.avgHoursToFinalAccept != null ? `${r.avgHoursToFinalAccept}h` : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
